import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  MenuItem,
  Select,
} from "@mui/material";
import classNames from "classnames";
import { isEmpty } from "lodash";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import * as XLSX from "xlsx";

function agentFullName(agent) {
  const name = [agent.firstName, agent.lastName].filter(Boolean).join(" ");
  return name || "";
}

function agentMobile(agent) {
  return [agent.countryCode, agent.mobile].filter(Boolean).join(" ") || "";
}

export default function QuizAgentsModal({
  open,
  onClose,
  quizTitle,
  loading,
  agentsAttempted,
  agentsNotAttempted,
  passOutcome = "",
  onPassOutcomeChange,
}) {
  const { t } = useTranslation();

  const handleExportSheet = useCallback(() => {
    const headersAttempted = [
      "#",
      t("O_USER_NAME"),
      "Email",
      "Mobile",
      t("O_DESIGNATION"),
      t("O_STATUS"),
      t("O_SCORE"),
      "Grade",
      "Grade Status",
      "Submission status",
    ];

    const rowsAttempted = (agentsAttempted || []).map((agent, i) => {
      const sr = agent.submissionResult;
      let scoreText = "";
      if (sr && sr.totalScore != null && sr.maxScore != null) {
        scoreText = `${sr.totalScore} / ${sr.maxScore}`;
      }
      return [
        i + 1,
        agentFullName(agent),
        agent.email ?? "",
        agentMobile(agent),
        agent.designation ?? "",
        agent.status ?? "",
        scoreText,
        sr?.percentage != null ? `${sr.percentage}%` : "",
        sr == null ? "" : sr.isPassed ? "Passed" : "Failed",
        sr == null
          ? ""
          : sr.status === "graded"
            ? "Graded"
            : "Pending",
      ];
    });

    const headersNotAttempted = [
      "#",
      t("O_USER_NAME"),
      "Email",
      "Mobile",
      t("O_DESIGNATION"),
      t("O_STATUS"),
    ];

    const rowsNotAttempted = (agentsNotAttempted || []).map((agent, i) => [
      i + 1,
      agentFullName(agent),
      agent.email ?? "",
      agentMobile(agent),
      agent.designation ?? "",
      agent.status ?? "",
    ]);

    const wb = XLSX.utils.book_new();
    const ws1 = XLSX.utils.aoa_to_sheet([headersAttempted, ...rowsAttempted]);
    XLSX.utils.book_append_sheet(wb, ws1, "Attempted");
    const ws2 = XLSX.utils.aoa_to_sheet([
      headersNotAttempted,
      ...rowsNotAttempted,
    ]);
    XLSX.utils.book_append_sheet(wb, ws2, "Not attempted");

    const safeBase =
      String(quizTitle || "quiz-export")
        .replace(/[/\\?%*:|"<>]/g, "-")
        .trim()
        .slice(0, 80) || "quiz-export";
    XLSX.writeFile(wb, `quiz-agents-${safeBase}.xlsx`);
  }, [
    quizTitle,
    agentsAttempted,
    agentsNotAttempted,
    t,
  ]);

  return (
    <Dialog fullWidth maxWidth="lg" open={open} onClose={onClose}>
      <DialogTitle>
        <span className="block">{t("O_AGENTS")}</span>
        {quizTitle ? (
          <span className="mt-1 block text-sm font-normal text-gray-600">
            {quizTitle}
          </span>
        ) : null}
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <div className="flex justify-center py-12">
            <CircularProgress />
          </div>
        ) : (
          <>
            <div className="mb-4 flex flex-wrap items-end gap-3">
              <div className="min-w-[200px] flex-1">
                <label className="block text-xs text-gray-600 mb-1">
                  Grade status
                </label>
                <Select
                  variant="outlined"
                  size="small"
                  className="w-full max-w-xs"
                  value={passOutcome}
                  onChange={(e) => onPassOutcomeChange?.(e.target.value)}
                  displayEmpty
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="passed">Passed</MenuItem>
                  <MenuItem value="failed">Failed</MenuItem>
                </Select>
              </div>
            </div>
            <div className="mb-6">
              <h2 className="text-sm font-semibold text-gray-900 mb-3">
                Attempted
              </h2>
              <div className="overflow-x-auto">
                <table className="w-full text-xs text-left text-[#A5A5A5] dark:text-gray-400">
                  <thead className="text-xs text-gray-900 border border-[#E1E6EE] bg-[#E1E6EE] dark:bg-gray-700 dark:text-gray-400">
                    <tr>
                      <th scope="col" className="py-3 px-6">
                        #
                      </th>
                      <th scope="col" className="py-3 px-6">
                        {t("O_USER_NAME")}
                      </th>
                      <th scope="col" className="py-3 px-6">
                        Email
                      </th>
                      <th scope="col" className="py-3 px-6">
                        Mobile
                      </th>
                      <th scope="col" className="py-3 px-6">
                        Designation
                      </th>
                      <th scope="col" className="py-3 px-6">
                        {t("O_STATUS")}
                      </th>
                      <th scope="col" className="py-3 px-6">
                        {t("O_SCORE")}
                      </th>
                      <th scope="col" className="py-3 px-6">
                        Grade
                      </th>
                      <th scope="col" className="py-3 px-6">
                        Grade Status
                      </th>
                      <th scope="col" className="py-3 px-6">
                        Submission status
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {agentsAttempted?.map((agent, i) => {
                      const sr = agent.submissionResult;
                      return (
                        <tr
                          key={agent._id}
                          className="border-b border-[#E1E6EE]"
                        >
                          <td className="py-3 px-6">{i + 1}</td>
                          <td className="py-3 px-6">
                            {[agent.firstName, agent.lastName]
                              .filter(Boolean)
                              .join(" ") || "—"}
                          </td>
                          <td className="py-3 px-6">{agent.email ?? "—"}</td>
                          <td className="py-3 px-6 whitespace-nowrap">
                            {[agent.countryCode, agent.mobile]
                              .filter(Boolean)
                              .join(" ") || "—"}
                          </td>
                          <td className="py-3 px-6">
                            {agent.designation ?? "—"}
                          </td>
                          <td className="py-3 px-6">{agent.status ?? "—"}</td>
                          <td className="py-3 px-6 whitespace-nowrap font-bold">
                            {sr &&
                            sr.maxScore != null &&
                            sr.totalScore != null ? (
                              sr._id ? (
                                <Link
                                  to={`/test-manager/submissions/details/${sr._id}`}
                                  className="text-green-400 hover:text-green-700 font-bold"
                                >
                                  {`${sr.totalScore} / ${sr.maxScore}`}
                                </Link>
                              ) : (
                                `${sr.totalScore} / ${sr.maxScore}`
                              )
                            ) : (
                              "—"
                            )}
                          </td>
                          <td className="py-3 px-6 whitespace-nowrap font-bold">
                            {sr?.percentage != null
                              ? `${sr.percentage}%`
                              : "—"}
                          </td>
                          <td
                            className={classNames(
                              "py-3 px-6 font-bold",
                              sr == null
                                ? ""
                                : sr.isPassed
                                  ? "text-green-500"
                                  : "text-red-500",
                            )}
                          >
                            {sr == null
                              ? "—"
                              : sr.isPassed
                                ? "Passed"
                                : "Failed"}
                          </td>
                          <td
                            className={classNames(
                              "py-3 px-6 font-bold",
                              sr?.status === "graded"
                                ? "text-green-500"
                                : sr
                                  ? "text-yellow-500"
                                  : "",
                            )}
                          >
                            {sr == null
                              ? "—"
                              : sr.status === "graded"
                                ? "Graded"
                                : "Pending"}
                          </td>
                        </tr>
                      );
                    })}
                    {isEmpty(agentsAttempted) && (
                      <tr className="bg-white border-b dark:bg-gray-800 dark:border-gray-700">
                        <td className="py-4 px-6 text-center" colSpan={10}>
                          {t("O_NO_RECORD_FOUND")}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <hr className="my-8 border-[#E1E6EE]" />

            <div>
              <h2 className="text-sm font-semibold text-gray-900 mb-3">
                Not attempted
              </h2>
              <div className="overflow-x-auto">
                <table className="w-full text-xs text-left text-[#A5A5A5] dark:text-gray-400">
                  <thead className="text-xs text-gray-900 border border-[#E1E6EE] bg-[#E1E6EE] dark:bg-gray-700 dark:text-gray-400">
                    <tr>
                      <th scope="col" className="py-3 px-6">
                        #
                      </th>
                      <th scope="col" className="py-3 px-6">
                        {t("O_USER_NAME")}
                      </th>
                      <th scope="col" className="py-3 px-6">
                        Email
                      </th>
                      <th scope="col" className="py-3 px-6">
                        Mobile
                      </th>
                      <th scope="col" className="py-3 px-6">
                        Designation
                      </th>
                      <th scope="col" className="py-3 px-6">
                        {t("O_STATUS")}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {agentsNotAttempted?.map((agent, i) => (
                      <tr
                        key={agent._id}
                        className="border-b border-[#E1E6EE]"
                      >
                        <td className="py-3 px-6">{i + 1}</td>
                        <td className="py-3 px-6">
                          {[agent.firstName, agent.lastName]
                            .filter(Boolean)
                            .join(" ") || "—"}
                        </td>
                        <td className="py-3 px-6">{agent.email ?? "—"}</td>
                        <td className="py-3 px-6 whitespace-nowrap">
                          {[agent.countryCode, agent.mobile]
                            .filter(Boolean)
                            .join(" ") || "—"}
                        </td>
                        <td className="py-3 px-6">
                          {agent.designation ?? "—"}
                        </td>
                        <td className="py-3 px-6">{agent.status ?? "—"}</td>
                      </tr>
                    ))}
                    {isEmpty(agentsNotAttempted) && (
                      <tr className="bg-white border-b dark:bg-gray-800 dark:border-gray-700">
                        <td className="py-4 px-6 text-center" colSpan={6}>
                          {t("O_NO_RECORD_FOUND")}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 2, pb: 2, gap: 1 }}>
        <Button
          onClick={handleExportSheet}
          variant="contained"
          disabled={loading}
        >
          Export
        </Button>
        <Button onClick={onClose} variant="outlined">
          {t("O_CLOSE")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
